import { useState, useEffect, useMemo, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Car, ChevronDown, Search, Loader2, Star, X } from 'lucide-react';
import { vehicles as vehiclesApi, devices as devicesApi } from '@/lib/trackizy';
import { vehiclesActions, devicesActions } from '@/store';
import { showToast } from '@/components/shared/Toast';

/**
 * Vehicle / Trackers management block for the DeviceDrawer Connections section.
 *
 * A car rental may fit 2–4 GPS trackers in one physical car; this panel groups
 * a device into a "vehicle" (one PRIMARY tracker + siblings). Matches the
 * <LinkPanel> collapsible glass style so it sits natively next to Geofences /
 * Drivers / Commands / Maintenance.
 *
 * After any membership mutation we (1) write the returned vehicle back into the
 * vehicles slice and (2) re-fetch + refresh the devices slice, because a
 * membership change can flip vehicleId / isPrimary on up to TWO devices (this
 * one and an auto-promoted sibling) — re-fetching is the robust way to reflect
 * that on the list / map immediately. This is a rare admin action, so the extra
 * fetch is fine.
 */
export default function VehicleTrackersPanel({ device }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const orgRole = useSelector((s) => s.org?.role);
  const isSultan = useSelector((s) => s.session.user?.is_sultan);
  const canWrite = isSultan || orgRole === 'admin' || orgRole === 'manager';

  const vehiclesById = useSelector((s) => s.vehicles.items);
  const devicesById = useSelector((s) => s.devices.items);

  // Read the LIVE device from Redux so the panel reflects the freshest
  // vehicleId / isPrimary after a re-fetch; fall back to the passed prop.
  const liveDevice = devicesById?.[device.id] || device;
  const vehicleId = liveDevice.vehicleId || null;
  const vehicle = vehicleId ? vehiclesById?.[vehicleId] : null;

  const [open, setOpen] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [listLoading, setListLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  const [addMode, setAddMode] = useState('pick'); // 'pick' | 'create'
  const [search, setSearch] = useState('');
  const [newLabel, setNewLabel] = useState('');

  // Load the vehicle catalog the first time the panel opens (the slice is
  // usually already seeded by socket.js bootstrap, but this makes the panel
  // self-sufficient and refreshes stale data).
  useEffect(() => {
    if (!open || loaded) return;
    let cancelled = false;
    setListLoading(true);
    vehiclesApi
      .list()
      .then((list) => {
        if (cancelled) return;
        dispatch(vehiclesActions.refresh(list || []));
        setLoaded(true);
      })
      .catch(() => { /* keep whatever the slice already holds */ })
      // Clear the spinner UNCONDITIONALLY. setLoaded(true) above re-runs this
      // effect (loaded is a dep), whose cleanup flips `cancelled` true — a
      // guarded finally would then skip this and leave the spinner stuck forever
      // (only visible when the org has 0 vehicles, i.e. the create-first-vehicle
      // flow). A setState after unmount is a harmless no-op in React 18.
      .finally(() => { setListLoading(false); });
    return () => { cancelled = true; };
  }, [open, loaded, dispatch]);

  const vehicleList = useMemo(
    () => Object.values(vehiclesById || {}),
    [vehiclesById],
  );

  const filteredVehicles = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return vehicleList;
    return vehicleList.filter(
      (v) =>
        String(v.label || '').toLowerCase().includes(q) ||
        String(v.plate || '').toLowerCase().includes(q),
    );
  }, [vehicleList, search]);

  // Push the returned vehicle into Redux, then re-fetch devices so the two
  // slices agree (membership alters vehicleId/isPrimary on possibly two rows).
  const applyResult = useCallback(
    async (returnedVehicle) => {
      if (returnedVehicle) dispatch(vehiclesActions.update(returnedVehicle));
      try {
        const fresh = await devicesApi.list();
        dispatch(devicesActions.refresh(fresh || []));
      } catch {
        // Devices slice keeps its current shape; the WS stream self-heals.
      }
    },
    [dispatch],
  );

  const addToExisting = useCallback(
    async (targetId) => {
      if (!targetId || busy) return;
      setBusy(true);
      try {
        const updated = await vehiclesApi.addTracker(targetId, device.id);
        await applyResult(updated);
        setSearch('');
        showToast(t('vehicles.trackerLinked', 'Tracker added to vehicle'));
      } catch (err) {
        showToast(`${t('vehicles.linkFailed', 'Could not add tracker')}: ${err.message}`);
      } finally {
        setBusy(false);
      }
    },
    [busy, device.id, applyResult, t],
  );

  const createAndLink = useCallback(async () => {
    const label = newLabel.trim();
    if (!label || busy) return;
    setBusy(true);
    try {
      // Two calls: create the vehicle, then link this tracker. Register the
      // (empty) vehicle immediately so a failure between the two still surfaces
      // it in the slice rather than orphaning it silently.
      const created = await vehiclesApi.create({ label });
      dispatch(vehiclesActions.update(created));
      const linked = await vehiclesApi.addTracker(created.id, device.id);
      await applyResult(linked);
      setNewLabel('');
      setAddMode('pick');
      showToast(t('vehicles.vehicleCreated', 'Vehicle created'));
    } catch (err) {
      showToast(`${t('vehicles.createFailed', 'Could not create vehicle')}: ${err.message}`);
    } finally {
      setBusy(false);
    }
  }, [newLabel, busy, device.id, dispatch, applyResult, t]);

  const setPrimary = useCallback(
    async (deviceId) => {
      if (!vehicle || busy) return;
      setBusy(true);
      try {
        const updated = await vehiclesApi.setPrimary(vehicle.id, deviceId);
        await applyResult(updated);
        showToast(t('vehicles.primarySet', 'Primary tracker updated'));
      } catch (err) {
        showToast(`${t('vehicles.primaryFailed', 'Could not set primary')}: ${err.message}`);
      } finally {
        setBusy(false);
      }
    },
    [vehicle, busy, applyResult, t],
  );

  const removeTracker = useCallback(
    async (deviceId) => {
      if (!vehicle || busy) return;
      setBusy(true);
      try {
        const updated = await vehiclesApi.removeTracker(vehicle.id, deviceId);
        await applyResult(updated);
        showToast(t('vehicles.trackerRemoved', 'Tracker removed from vehicle'));
      } catch (err) {
        showToast(`${t('vehicles.removeFailed', 'Could not remove tracker')}: ${err.message}`);
      } finally {
        setBusy(false);
      }
    },
    [vehicle, busy, applyResult, t],
  );

  const trackers = vehicle?.trackers || [];
  const headerHint = vehicleId
    ? vehicle
      ? vehicle.label
      : t('vehicles.inAVehicle', 'In a vehicle')
    : t('vehicles.notLinked', 'Not in a vehicle');

  return (
    <div className="border border-slate-200/80 dark:border-slate-800 rounded-2xl overflow-hidden">
      {/* Header */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full px-5 py-3 flex items-center justify-between bg-white dark:bg-slate-900 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
      >
        <div className="flex items-center gap-2 min-w-0">
          <Car size={14} className="text-slate-400 shrink-0" />
          <span className="text-xs font-bold uppercase tracking-wider text-slate-700 dark:text-slate-300">
            {t('vehicles.vehicleTrackers', 'Vehicle / Trackers')}
          </span>
          <span className="text-[10px] text-slate-400 ms-1 truncate">
            <bdi>{headerHint}</bdi>
          </span>
        </div>
        <ChevronDown
          size={14}
          className={`text-slate-400 shrink-0 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
        />
      </button>

      {/* Expanded content */}
      {open && (
        <div className="border-t border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-900 px-4 py-3">
          {listLoading && !vehicle && vehicleList.length === 0 ? (
            <div className="flex items-center justify-center py-6">
              <Loader2 size={18} className="animate-spin text-slate-400" />
            </div>
          ) : vehicleId ? (
            /* ── This device IS in a vehicle ────────────────────────── */
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-slate-800 dark:text-slate-200 truncate">
                  <bdi>{vehicle?.label || t('vehicles.inAVehicle', 'In a vehicle')}</bdi>
                </span>
                {vehicle?.plate && (
                  <span className="text-[11px] text-slate-400">
                    <bdi>{vehicle.plate}</bdi>
                  </span>
                )}
              </div>

              {/* Member trackers */}
              <div className="space-y-0.5">
                {trackers.map((tr) => {
                  const isThis = String(tr.deviceId) === String(device.id);
                  return (
                    <div
                      key={tr.deviceId}
                      className="flex items-center gap-2 px-1 py-1.5 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800"
                    >
                      {tr.isPrimary ? (
                        <Star size={14} className="text-amber-400 fill-amber-400 shrink-0" />
                      ) : (
                        <Star size={14} className="text-slate-300 dark:text-slate-600 shrink-0" />
                      )}
                      <span className="text-sm text-slate-700 dark:text-slate-300 truncate">
                        <bdi>{tr.deviceName || `#${tr.deviceId}`}</bdi>
                      </span>
                      {tr.uniqueId && (
                        <span className="text-[11px] text-slate-400 truncate">
                          <bdi>{tr.uniqueId}</bdi>
                        </span>
                      )}
                      {isThis && (
                        <span className="text-[10px] text-slate-400 italic shrink-0">
                          {t('vehicles.thisTracker', '(this tracker)')}
                        </span>
                      )}
                      <div className="ms-auto flex items-center gap-1 shrink-0">
                        {!tr.isPrimary && canWrite && (
                          <button
                            type="button"
                            onClick={() => setPrimary(tr.deviceId)}
                            disabled={busy}
                            className="text-[11px] font-medium text-blue-600 dark:text-blue-400 hover:underline disabled:opacity-40"
                          >
                            {t('vehicles.makePrimary', 'Make primary')}
                          </button>
                        )}
                        {canWrite && (
                          <button
                            type="button"
                            onClick={() => removeTracker(tr.deviceId)}
                            disabled={busy}
                            title={t('vehicles.removeFromVehicle', 'Remove from vehicle')}
                            className="p-1 rounded-md text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 disabled:opacity-40 transition-colors"
                          >
                            <X size={14} />
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {canWrite ? (
                <div className="pt-1">
                  {!liveDevice.isPrimary && (
                    <button
                      type="button"
                      onClick={() => setPrimary(device.id)}
                      disabled={busy}
                      className="text-xs font-semibold text-blue-600 dark:text-blue-400 hover:underline disabled:opacity-40"
                    >
                      {t('vehicles.makeThisPrimary', 'Make this the primary tracker')}
                    </button>
                  )}
                </div>
              ) : (
                <p className="text-[11px] text-slate-400 pt-1">
                  {t('vehicles.readOnlyHint', 'You do not have permission to change trackers.')}
                </p>
              )}
            </div>
          ) : (
            /* ── This device is NOT in a vehicle ────────────────────── */
            <div className="space-y-3">
              {!canWrite ? (
                <p className="text-sm text-slate-400 text-center py-3">
                  {t('vehicles.notLinkedReadOnly', 'This tracker is not part of a vehicle.')}
                </p>
              ) : (
                <>
                  {/* Mode toggle */}
                  <div className="flex gap-1 p-0.5 bg-slate-100 dark:bg-slate-800 rounded-lg">
                    <button
                      type="button"
                      onClick={() => setAddMode('pick')}
                      className={`flex-1 text-xs font-semibold py-1.5 rounded-md transition-colors ${
                        addMode === 'pick'
                          ? 'bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-100 shadow-sm'
                          : 'text-slate-500 dark:text-slate-400'
                      }`}
                    >
                      {t('vehicles.addToExisting', 'Add to existing')}
                    </button>
                    <button
                      type="button"
                      onClick={() => setAddMode('create')}
                      className={`flex-1 text-xs font-semibold py-1.5 rounded-md transition-colors ${
                        addMode === 'create'
                          ? 'bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-100 shadow-sm'
                          : 'text-slate-500 dark:text-slate-400'
                      }`}
                    >
                      {t('vehicles.createNew', 'Create new')}
                    </button>
                  </div>

                  {addMode === 'pick' ? (
                    <div className="space-y-2">
                      <div className="relative">
                        <Search size={14} className="absolute start-3 top-1/2 -translate-y-1/2 text-slate-400" />
                        <input
                          type="text"
                          value={search}
                          onChange={(e) => setSearch(e.target.value)}
                          placeholder={t('vehicles.searchVehicles', 'Search vehicles…')}
                          className="w-full ps-9 pe-3 py-1.5 text-sm border border-slate-200 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 placeholder:text-slate-400"
                        />
                      </div>
                      {vehicleList.length === 0 ? (
                        <p className="text-sm text-slate-400 text-center py-3">
                          {t('vehicles.noVehiclesYet', 'No vehicles yet — create one.')}
                        </p>
                      ) : filteredVehicles.length === 0 ? (
                        <p className="text-sm text-slate-400 text-center py-3">
                          {t('settings.noResults', 'No results')}
                        </p>
                      ) : (
                        <div className="space-y-0.5 max-h-48 overflow-y-auto">
                          {filteredVehicles.map((v) => (
                            <button
                              type="button"
                              key={v.id}
                              onClick={() => addToExisting(v.id)}
                              disabled={busy}
                              className="w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-start hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-40 transition-colors"
                            >
                              <Car size={13} className="text-slate-400 shrink-0" />
                              <span className="text-sm text-slate-700 dark:text-slate-300 truncate">
                                <bdi>{v.label || `#${v.id}`}</bdi>
                              </span>
                              {v.plate && (
                                <span className="text-[11px] text-slate-400 truncate">
                                  <bdi>{v.plate}</bdi>
                                </span>
                              )}
                              <span className="ms-auto text-[10px] text-slate-400 shrink-0">
                                {(v.trackers?.length || 0)}
                              </span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={newLabel}
                        onChange={(e) => setNewLabel(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') { e.preventDefault(); createAndLink(); }
                        }}
                        placeholder={t('vehicles.vehicleLabel', 'Vehicle label')}
                        className="flex-1 min-w-0 px-3 py-1.5 text-sm border border-slate-200 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 placeholder:text-slate-400"
                      />
                      <button
                        type="button"
                        onClick={createAndLink}
                        disabled={busy || !newLabel.trim()}
                        className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-40 transition-colors"
                      >
                        {busy && <Loader2 size={13} className="animate-spin" />}
                        {t('vehicles.createAndAdd', 'Create & add')}
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
